import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';

interface Opcao {
  id: number;
  descricao: string;
}

interface ContaPagarItem {
  id: number;
  finFornecedorRecebedor: string;
  finDocumento: string;
  previsaoReal: string;
  idCentroCusto: number;
  idPlanoContas: number;
  centroCusto: string;
  planoContas: string;
  parcela: number;
  dataEmissao: string;
  dataVencimento: string;
  dataPagamento: string;
  valorDocumento: number | null;
  valorPago: number | null;
  pago: boolean;
  tipoPagamento: string;
  observacao: string;
  dataEmissaoOrdenacao: number;
}

@Component({
  selector: 'app-consultar-contas-pagar',
  templateUrl: './consultar-contas-pagar.component.html',
  styleUrls: ['./consultar-contas-pagar.component.scss']
})
export class ConsultarContasPagarComponent implements OnInit {
  itens: ContaPagarItem[] = [];
  itensPaginados: ContaPagarItem[] = [];

  documentos: Opcao[] = [];
  fornecedores: Opcao[] = [];
  centrosCustos: Opcao[] = [];
  planosContas: Opcao[] = [];
  tiposPagamento: Opcao[] = [];

  filtroIdFinDocumento = 0;
  filtroParcela: number | null = null;
  filtroIdFinFornecedorRecebedor = 0;
  filtroDataEmissao = '';
  filtroDataVencimento = '';
  filtroDataPagamento = '';
  filtroIdTipoPagamento = 0;
  filtroObservacao = '';
  filtroPrevisaoReal: 'T' | 'P' | 'R' = 'T';

  paginaAtual = 1;
  itensPorPagina = 10;
  loading = false;
  arrastandoTabela = false;
  private posicaoInicialX = 0;
  private scrollInicialX = 0;

  constructor(
    private ajaxService: AjaxService,
    private util: UtilService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.carregarOpcoes();
    this.carregarCentrosCustos();
    this.buscar();
  }

  get totalPaginas(): number {
    return Math.ceil(this.itens.length / this.itensPorPagina) || 1;
  }

  private carregarCentrosCustos(): void {
    this.ajaxService.get(`${API}financeiro/centrocustos`).subscribe({
      next: (response) => {
        if (response?.hasErrors) {
          return;
        }
        this.centrosCustos = this.util.mapearOpcoesCentroCusto(this.util.extrairListaResponse(response));
        this.atualizarDescricoesTabela();
      }
    });
  }

  carregarOpcoes(): void {
    this.ajaxService.get(`${API}financeiro/FinContaPagar/ConsultarOpcoes`).subscribe({
      next: (response) => {
        const data = this.desempacotarResponse(response);
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível carregar as opções.');
          return;
        }

        const documentosData = data.documentos || data.docs || [];
        const fornecedoresData = data.fornecedores || data.fornecedor || [];
        const centrosCustosData = data.centroscustos || data.centros_custos || data.centros || [];
        const planosContasData = data.planoscontas || data.planos_contas || [];
        const tiposPagamentoData = data.tipospagamento || data.tipopagamento || data.tiposPagamento || [];

        this.documentos = this.normalizarLista(documentosData)
          .map((x: any) => ({ id: Number(x.id || 0), descricao: String(x.descricao || '') }))
          .filter((x: Opcao) => x.id > 0);
        this.fornecedores = this.normalizarLista(fornecedoresData)
          .map((x: any) => ({ id: Number(x.id || 0), descricao: String(x.descricao || '') }))
          .filter((x: Opcao) => x.id > 0);
        this.centrosCustos = this.util.mapearOpcoesCentroCusto(this.normalizarLista(centrosCustosData));
        this.planosContas = this.normalizarLista(planosContasData)
          .map((x: any) => ({ id: Number(x.id || 0), descricao: String(x.descricao || '') }))
          .filter((x: Opcao) => x.id > 0);
        this.tiposPagamento = this.normalizarLista(tiposPagamentoData)
          .map((x: any) => ({ id: Number(x.id || 0), descricao: String(x.descricao || '') }))
          .filter((x: Opcao) => x.id > 0);

        this.atualizarDescricoesTabela();
      },
      error: () => {
        this.util.toasterShowError('Erro', 'Falha ao carregar Documento, Fornecedor e Tipo de Pagamento.');
      }
    });
  }

  novo(): void {
    this.router.navigate(['/contas-pagar']);
  }

  buscar(): void {
    const params = new URLSearchParams();
    if (this.filtroIdFinDocumento > 0) params.set('idFinDocumento', String(this.filtroIdFinDocumento));
    if (this.filtroParcela != null) params.set('parcela', String(this.filtroParcela));
    if (this.filtroIdFinFornecedorRecebedor > 0) params.set('idFinFornecedorRecebedor', String(this.filtroIdFinFornecedorRecebedor));
    if (this.filtroDataEmissao) params.set('dataEmissao', this.filtroDataEmissao);
    if (this.filtroDataVencimento) params.set('dataVencimento', this.filtroDataVencimento);
    if (this.filtroDataPagamento) params.set('dataPagamento', this.filtroDataPagamento);
    if (this.filtroIdTipoPagamento > 0) params.set('idTipoPagamento', String(this.filtroIdTipoPagamento));
    if (this.filtroObservacao.trim()) params.set('observacao', this.filtroObservacao.trim());

    const query = params.toString();
    const url = `${API}financeiro/FinContaPagar/ConsultarFinContaPagar${query ? `?${query}` : ''}`;

    this.loading = true;
    this.util.showLoading();
    this.ajaxService.get(url).subscribe({
      next: (response) => {
        this.loading = false;
        this.util.closeLoading();
        const lista = this.extrairListaResponse(response);
        this.itens = lista.map((x: any) => {
          const item = this.util.converterParaLowercase(x || {});
          return {
            id: Number(item.id || 0),
            finFornecedorRecebedor: String(item.finfornecedorrecebedor || ''),
            finDocumento: String(item.findocumento || ''),
            previsaoReal: String(item.previsaoreal || 'R').toUpperCase() === 'P' ? 'P' : 'R',
            idCentroCusto: Number(item.idcentrocusto || 0),
            idPlanoContas: Number(item.idplanocontas || 0),
            centroCusto: this.resolverDescricaoPorId(this.centrosCustos, Number(item.idcentrocusto || 0)),
            planoContas: this.resolverDescricaoPorId(this.planosContas, Number(item.idplanocontas || 0)),
            parcela: Number(item.parcela || 0),
            dataEmissao: this.formatarData(item.dataemissao),
            dataVencimento: this.formatarData(item.datavencimento),
            dataPagamento: this.formatarData(item.datapagamento),
            valorDocumento: item.valordocumento != null ? Number(item.valordocumento) : null,
            valorPago: item.valorpago != null ? Number(item.valorpago) : null,
            pago: Boolean(item.pago),
            tipoPagamento: String(item.tipopagamento || ''),
            observacao: String(item.observacao || ''),
            dataEmissaoOrdenacao: this.obterTimestampData(item.dataemissao)
          } as ContaPagarItem;
        })
          .filter((x: ContaPagarItem) => this.filtroPrevisaoReal === 'T' || x.previsaoReal === this.filtroPrevisaoReal)
          .filter((x: ContaPagarItem) => x.id > 0)
          .sort((a: ContaPagarItem, b: ContaPagarItem) => {
            if (a.dataEmissaoOrdenacao !== b.dataEmissaoOrdenacao) {
              return b.dataEmissaoOrdenacao - a.dataEmissaoOrdenacao;
            }
            return b.id - a.id;
          });

        this.paginaAtual = 1;
        this.atualizarPaginacao();
      },
      error: () => {
        this.loading = false;
        this.util.closeLoading();
        this.itens = [];
        this.itensPaginados = [];
        this.util.toasterShowError('Erro', 'Não foi possível carregar contas a pagar.');
      }
    });
  }

  editar(item: ContaPagarItem): void {
    this.router.navigate(['/contas-pagar'], { queryParams: { id: item.id } });
  }

  pagamento(item: ContaPagarItem): void {
    this.router.navigate(['/contas-pagar'], { queryParams: { id: item.id, pagamento: true } });
  }

  deletar(item: ContaPagarItem): void {
    this.util.showLoading();
    this.ajaxService.post(`${API}financeiro/FinContaPagar/Deletar`, { id: item.id }).subscribe({
      next: (response) => {
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível excluir.');
          return;
        }
        this.util.toasterShowSuccess('Sucesso', 'Registro excluído com sucesso!');
        this.buscar();
      },
      error: () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao excluir conta a pagar.');
      }
    });
  }

  limparFiltros(): void {
    this.filtroIdFinDocumento = 0;
    this.filtroParcela = null;
    this.filtroIdFinFornecedorRecebedor = 0;
    this.filtroDataEmissao = '';
    this.filtroDataVencimento = '';
    this.filtroDataPagamento = '';
    this.filtroIdTipoPagamento = 0;
    this.filtroObservacao = '';
    this.filtroPrevisaoReal = 'T';
    this.buscar();
  }

  filtrarPorPrevisaoReal(tipo: 'T' | 'P' | 'R'): void {
    this.filtroPrevisaoReal = tipo;
    this.buscar();
  }

  paginaAnterior(): void {
    if (this.paginaAtual > 1) {
      this.paginaAtual--;
      this.atualizarPaginacao();
    }
  }

  proximaPagina(): void {
    if (this.paginaAtual < this.totalPaginas) {
      this.paginaAtual++;
      this.atualizarPaginacao();
    }
  }

  alterarItensPorPagina(): void {
    this.paginaAtual = 1;
    this.atualizarPaginacao();
  }

  iniciarArrasteTabela(event: MouseEvent, container: HTMLElement): void {
    if (event.button !== 0) return;
    const alvo = event.target as HTMLElement;
    if (alvo?.closest('button, a, input, select, textarea, label')) return;
    this.arrastandoTabela = true;
    this.posicaoInicialX = event.clientX;
    this.scrollInicialX = container.scrollLeft;
  }

  moverArrasteTabela(event: MouseEvent, container: HTMLElement): void {
    if (!this.arrastandoTabela) return;
    const deslocamento = event.clientX - this.posicaoInicialX;
    container.scrollLeft = this.scrollInicialX - deslocamento;
    event.preventDefault();
  }

  encerrarArrasteTabela(): void {
    this.arrastandoTabela = false;
  }

  private atualizarPaginacao(): void {
    const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
    const fim = inicio + this.itensPorPagina;
    this.itensPaginados = this.itens.slice(inicio, fim);
  }

  private formatarData(value: any): string {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('pt-BR');
  }

  private obterTimestampData(value: any): number {
    if (!value) return 0;
    const texto = String(value).trim();
    const padraoBr = texto.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (padraoBr) {
      const dia = Number(padraoBr[1]);
      const mes = Number(padraoBr[2]);
      const ano = Number(padraoBr[3]);
      return new Date(ano, mes - 1, dia).getTime();
    }

    const data = new Date(texto);
    const timestamp = data.getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  private normalizarLista(data: any): any[] {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'object') {
      if (Object.prototype.hasOwnProperty.call(data, 'id') || Object.prototype.hasOwnProperty.call(data, 'descricao')) return [data];
      return Object.values(data).filter((x: any) => x && typeof x === 'object');
    }
    return [];
  }

  private desempacotarResponse(response: any): any {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    return this.util.converterParaLowercase(atual ?? {});
  }

  private extrairListaResponse(response: any): any[] {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (Array.isArray(atual)) break;
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    return this.normalizarLista(atual);
  }

  private resolverDescricaoPorId(lista: Opcao[], id: number): string {
    if (!id || id <= 0) return '-';
    const encontrado = lista.find((x) => x.id === id);
    return encontrado?.descricao || '-';
  }

  private atualizarDescricoesTabela(): void {
    if (!this.itens?.length) return;
    this.itens = this.itens.map((item) => ({
      ...item,
      centroCusto: this.resolverDescricaoPorId(this.centrosCustos, item.idCentroCusto),
      planoContas: this.resolverDescricaoPorId(this.planosContas, item.idPlanoContas)
    }));
    this.atualizarPaginacao();
  }
}
